import React from 'react';
import { SelectOptionElement } from '@odin-labs/components';
import { getSingleOrNull, typedAs } from 'utils';
import { IdCardIcon } from 'components/icons';
import {
  FormInputTypes,
  GridColSpan,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
} from 'components/form';
import { QrCodeField, QrCodeFieldProps } from 'components/qrCodeField';
import { validateScannedBadgeQrCode } from 'containers/utils';
import { WorkerCardFormatOption } from 'containers/worker/helpers/utils';
import {
  CARD_WITH_SCANNABLE_NUMBER,
  getBadgeNumberValidation,
} from 'containers/worker/modals/badgeModal/utils';
import { CreateBadgeInfo } from './types';

const getFormInputs = (args: {
  proximityCardFormatOptions: SelectOptionElement[];
  allowScanning: boolean;
  encoding?: string;
}): TypedFormInputs<CreateBadgeInfo> => {
  const { proximityCardFormatOptions, allowScanning, encoding } = args;
  return {
    proximityCardFormatId: {
      element: FormInputTypes.Select,
      label: 'Card format',
      elementProps: {
        disabled: (proximityCardFormatOptions?.length ?? 0) < 2,
        options: proximityCardFormatOptions ?? [],
        isClearable: false,
      },
      validation: { required: true },
      layout: GridColSpan.SpanFull,
    },
    badgeNumber: {
      element: FormInputTypes.CustomInput,
      label: 'Badge number',
      elementProps: {
        customInput: QrCodeField,
        // @ts-expect-error [it's a fine icon]
        icon: IdCardIcon,
        placeholder: allowScanning ? 'Enter or scan badge number' : null,
        ...typedAs<Omit<QrCodeFieldProps, 'name'>>({
          allowScanning,
          parseQrCode: validateScannedBadgeQrCode,
        }),
      },
      // Only the encoding's card number limit: the API checks the format's
      // pattern after stripping leading zeros, which this form leaves to it.
      validation: getBadgeNumberValidation({ encoding }),
      layout: GridColSpan.SpanFull,
    },
  };
};

export const getFormInputsHook =
  (args: {
    proximityCardFormatOptions: SelectOptionElement[];
  }): UseInputs<CreateBadgeInfo> =>
  (form: UseFormMethods<CreateBadgeInfo>): TypedFormInputs<CreateBadgeInfo> => {
    const { proximityCardFormatOptions } = args;
    const { watch } = form;
    const proximityCardFormatIdElement = watch('proximityCardFormatId') as
      | WorkerCardFormatOption
      | null
      | undefined;
    const allowScanning =
      proximityCardFormatIdElement?.name === CARD_WITH_SCANNABLE_NUMBER;

    React.useEffect(() => {
      if (proximityCardFormatIdElement?.name === CARD_WITH_SCANNABLE_NUMBER) {
        const badgeNumberFieldRef = form.getFieldRef('badgeNumber');
        badgeNumberFieldRef?.open?.();
      }
    }, [proximityCardFormatIdElement?.name]);

    return React.useMemo(
      () =>
        getFormInputs({
          proximityCardFormatOptions,
          allowScanning,
          encoding: proximityCardFormatIdElement?.encoding,
        }),
      [
        proximityCardFormatOptions,
        allowScanning,
        proximityCardFormatIdElement?.encoding,
      ],
    );
  };

export const getFormDefaultValues = (args: {
  proximityCardFormatOptions: SelectOptionElement[];
  defaultProximityCardFormatOption: SelectOptionElement;
}): CreateBadgeInfo => {
  const { proximityCardFormatOptions, defaultProximityCardFormatOption } = args;
  return {
    proximityCardFormatId:
      defaultProximityCardFormatOption ??
      getSingleOrNull(proximityCardFormatOptions),
    badgeNumber: '',
  };
};
